import { Button, Form, Spinner } from '@heroui/react'
import type { FileListingItemDto } from '@qse/api'
import { useBulkMoveFiles, useCreateFolder, useLookupFile } from '@qse/api'
import { queryKeys } from '@qse/api'
import { type ModalComponentProps } from '@qse/constants'
import { HeroInput } from '@qse/ui'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Validation function for folder name
const validateFolderName = (value: string) => {
  if (!value || value.trim() === '') {
    return 'Folder name cannot be empty'
  }
  if (value.length < 1) {
    return 'Folder name must be at least 1 character'
  }
  if (value.length > 255) {
    return 'Folder name cannot exceed 255 characters'
  }

  // Check for invalid characters that are not allowed in file/folder names
  // eslint-disable-next-line no-control-regex
  const invalidChars = /[<>:"/\\|?\x00-\x1f]/g

  if (invalidChars.test(value)) {
    return 'Folder name contains invalid characters (< > : " / \\ | ? or control characters)'
  }

  // Check for reserved names (Windows)
  const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i

  if (reservedNames.test(value)) {
    return 'Folder name cannot be a reserved system name'
  }

  // Check for names ending with dot or space
  if (value.endsWith('.') || value.endsWith(' ')) {
    return 'Folder name cannot end with a dot or space'
  }

  return true
}

interface CreateFolderModalData {
  parentId?: string
  parentPath?: string
  parentName?: string // Add parent folder name
  onSuccess?: (folderName: string, createdFolder?: FileListingItemDto) => void
  fileIds?: string[] // File IDs to move after folder creation
}

export default function CreateFolder(props: ModalComponentProps) {
  const { data, onClose } = props
  const modalData = data as CreateFolderModalData
  const createFolderMutation = useCreateFolder()
  const bulkMoveMutation = useBulkMoveFiles()
  const queryClient = useQueryClient()
  const [folderName, setFolderName] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce the folder name for API calls (500ms delay)
  const debouncedFolderName = useDebounce(folderName, 500)

  // Use useLookupFile hook for real-time conflict detection with debounced value
  const lookupParams = useMemo(() => {
    if (!debouncedFolderName.trim()) return null

    return {
      name: debouncedFolderName.trim(),
      parentId: modalData.parentId,
    }
  }, [debouncedFolderName, modalData.parentId, modalData.parentPath])

  const { data: lookupResult, isLoading: isCheckingConflict } = useLookupFile(
    lookupParams || {
      name: '',
      parentId: undefined,
      parentPath: undefined,
    },
  )

  // Invalidate lookup cache when modal opens to ensure fresh checks
  useEffect(() => {
    // Clear any existing lookup cache when modal opens
    queryClient.removeQueries({
      queryKey: queryKeys.files.lookup('', ''),
    })
  }, [queryClient])

  // Force refetch when debounced value changes to ensure fresh data
  useEffect(() => {
    if (debouncedFolderName.trim() && lookupParams) {
      // Invalidate the specific lookup query to force a fresh request
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.lookup(
          debouncedFolderName.trim(),
          modalData.parentId || modalData.parentPath || '',
        ),
      })
    }
  }, [debouncedFolderName, lookupParams, queryClient, modalData.parentId, modalData.parentPath])

  // Check for naming conflicts and validate folder name
  useEffect(() => {
    if (!debouncedFolderName.trim()) {
      setValidationError(null)

      return
    }

    // First check for naming conflicts
    if (lookupResult?.file) {
      setValidationError('A folder with this name already exists in this directory')

      return
    }

    // Then validate the folder name format
    const validationResult = validateFolderName(debouncedFolderName)

    if (validationResult !== true) {
      setValidationError(validationResult)
    } else {
      setValidationError(null)
    }
  }, [lookupResult, debouncedFolderName])

  // Validate folder name on change
  const handleFolderNameChange = useCallback((value: string) => {
    setFolderName(value)

    // Validate the folder name
    const validationResult = validateFolderName(value)

    if (validationResult !== true) {
      setValidationError(validationResult)
    } else {
      setValidationError(null)
    }
  }, [])

  // Check if create button should be disabled
  const isCreateDisabled = useMemo(() => {
    // If user has typed something, we need to wait for backend validation
    if (folderName.trim()) {
      // Button should be disabled if:
      // 1. Still checking for conflicts
      // 2. Has validation errors
      // 3. No lookup result yet (backend hasn't responded)
      // 4. Any pending operations
      return (
        isCheckingConflict ||
        !!validationError ||
        !lookupResult ||
        createFolderMutation.isPending ||
        bulkMoveMutation.isPending
      )
    }

    // If user hasn't typed anything, button is disabled
    return !folderName.trim() || createFolderMutation.isPending || bulkMoveMutation.isPending
  }, [
    folderName,
    createFolderMutation.isPending,
    bulkMoveMutation.isPending,
    validationError,
    isCheckingConflict,
    lookupResult,
  ])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const values = Object.fromEntries(formData)

    // Validate folder name is not empty
    const folderName = values.folderName as string

    if (!folderName || folderName.trim() === '') {
      toast.error('Folder name cannot be empty')

      return
    }

    // Check for validation errors
    if (validationError) {
      toast.error(validationError)

      return
    }

    try {
      // Prepare the folder creation data with parentId if available
      const folderData: { name: string; parentId?: string } = {
        name: folderName,
      }

      // Add parentId if provided in modal data
      if (modalData.parentId) {
        folderData.parentId = modalData.parentId
        console.log('Creating folder inside parent folder:', modalData.parentId)
      } else {
        console.log('Creating folder in root directory')
      }

      const response = await createFolderMutation.mutateAsync(folderData)

      // If file IDs are provided, move files to the newly created folder
      if (modalData.fileIds && modalData.fileIds.length > 0) {
        try {
          await bulkMoveMutation.mutateAsync({
            fileIds: modalData.fileIds,
            targetParentId: response.folder.id,
          })

          toast.success(
            `Folder "${folderName}" created and ${modalData.fileIds.length} file(s) moved successfully!`,
          )
        } catch (moveError) {
          console.error('Error moving files to new folder:', moveError)
          toast.error('Folder created but failed to move files. Please try moving them manually.')
        }
      } else {
        const locationMessage = modalData.parentId ? ' in current folder' : ' in root directory'

        toast.success(`Folder "${folderName}" created successfully${locationMessage}!`)
      }

      // Reset form
      setFolderName('')
      setValidationError(null)

      // Call onSuccess callback before closing the modal
      if (modalData.onSuccess) {
        modalData.onSuccess(folderName, response.folder)
      }

      // Always close all modals and reset modal states when folder creation is complete
      // onClose()
      onClose()
    } catch (error) {
      console.error('Error creating folder:', error)
      toast.error('Failed to create folder. Please try again.')
    }
  }

  const handleCancel = () => {
    // Always close all modals and reset modal states when canceling
    onClose()
  }

  // Auto-focus on input when modal opens
  useEffect(() => {
    // Only run when modal is actually open
    if (!props.toggleModel) return

    // Use a delay that accounts for modal animation (300ms) plus some buffer
    const timer = setTimeout(() => {
      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus()
          // Select all text if there's any content
          inputRef.current.select()

          return true
        }

        return false
      }

      // Try to focus with multiple attempts
      let attempts = 0
      const maxAttempts = 5

      const attemptFocus = () => {
        attempts++
        if (focusInput() || attempts >= maxAttempts) {
          return
        }
        // Try again with increasing delay
        setTimeout(attemptFocus, 100 * attempts)
      }

      attemptFocus()
    }, 400) // 300ms animation + 100ms buffer

    return () => clearTimeout(timer)
  }, [props.toggleModel]) // Re-run when modal opens/closes

  return (
    <div>
      {modalData.parentName ? (
        <div className="mb-4 text-sm text-gray-600">
          Create new folder in <span className="font-medium">{modalData.parentName}</span>
        </div>
      ) : modalData.parentId ? (
        <div className="mb-4 text-sm text-gray-600">
          Create new folder in <span className="font-medium">current folder</span>
        </div>
      ) : (
        <div className="mb-4 text-sm text-gray-600">
          Create new folder in <span className="font-medium">root directory</span>
        </div>
      )}
      <Form
        className="flex flex-col gap-5 py-5"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        <div className="space-y-2 w-full">
          <HeroInput
            ref={inputRef}
            isRequired
            control="text"
            description={folderName.length > 0 ? `${folderName.length}/255 characters` : undefined}
            errorMessage={validationError}
            isInvalid={!!validationError}
            label="Folder Name"
            maxLength={255}
            name="folderName"
            placeholder="New Folder"
            radius="sm"
            size="md"
            value={folderName}
            variant="bordered"
            endContent={
              <>
                <div className="flex items-center gap-2">
                  {isCheckingConflict && debouncedFolderName.trim() && (
                    <Spinner color="primary" size="sm" />
                  )}
                </div>
              </>
            }
            onChange={handleFolderNameChange}
          />
        </div>

        <div className="flex justify-end gap-3 w-full">
          <Button
            isDisabled={createFolderMutation.isPending || bulkMoveMutation.isPending}
            radius="sm"
            variant="flat"
            onPress={handleCancel}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            isDisabled={isCreateDisabled}
            isLoading={createFolderMutation.isPending || bulkMoveMutation.isPending}
            radius="sm"
            type="submit"
          >
            Create
          </Button>
        </div>
      </Form>
    </div>
  )
}
